import {
  Button,
  Form,
  Input,
  Modal,
  Radio,
} from "antd";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { patientFields } from "../../constant/data";
import {
  useCreatePatientDataMutation,
} from "../../redux/features/routeQuestionApi";

// Country is no longer selectable — defaults to the US ("US" ISO alpha-2 code)
const DEFAULT_COUNTRY = "US";

const { TextArea } = Input;



export default function StepPatientInfo({ formData, setFormData, onNext, onBack }) {
  const [form] = Form.useForm();
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [createPatientData, { isLoading }] = useCreatePatientDataMutation();

  useEffect(() => {
    // Keep mobile value as plain 10-digit text
    const mobileValue = formData?.mobile || form.getFieldValue("mobile");
    if (mobileValue) {
      const digits = String(mobileValue).replace(/\D/g, "").slice(-10);
      form.setFieldsValue({ mobile: digits });
    }
  }, [form, formData]);

  const handlePhoneChange = (e) => {
    const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 10);
    form.setFieldsValue({ mobile: digits });
  };

  const handleDobChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); 
    if (value.length > 8) value = value.slice(0, 8); 

    let formatted = value;
    if (value.length > 4) {
      formatted = value.replace(
        /^(\d{2})(\d{2})(\d{0,4}).*/,
        (_, m, d, y) => `${m}-${d}${y ? "-" + y : ""}`
      );
    } else if (value.length > 2) {
      formatted = value.replace(/^(\d{2})(\d{0,2})/, "$1-$2");
    }

    form.setFieldsValue({ dob: formatted });
  };

  const submitPatientInfo = async (values) => {
    if (values.dob) {
      const [month, day, year] = values.dob.split("-");
      values.dob = `${year}-${month}-${day}`;
    }

    const numericMobile = values.mobile.replace(/\D/g, "").slice(0, 10);

    const payload = {
      email: values.email,
      mobile: numericMobile,
      first_name: values.first_name,
      last_name: values.last_name,
      visit_type_id: formData?.service?.visit_type_id,
      visit_type: formData?.category === "established" ? "establish" : "intial",
      dob: values.dob,
      gender: values.gender,
      address: {
        country: DEFAULT_COUNTRY,
        state: values.state,
        zip_code: values.zip_code,
        address_line1: values.address_line1,
        address_line2: values.address_line2,
        city: values.city,
      },
    };

    try {
      const res = await createPatientData(payload).unwrap();
      toast.success("Patient data saved successfully");
      setFormData({ ...formData, ...res });
      onNext();
    } catch (err) {
      const apiMessage =
        err?.data?.message || err?.error || "Failed to save patient";
      toast.error(apiMessage);
    }
  };

  const handleFinish = (values) => {
    setPendingValues(values);
    setIsWarningModalOpen(true);
  };

  const handleWarningConfirm = async () => {
    if (!pendingValues) {
      setIsWarningModalOpen(false);
      return;
    }
    setIsWarningModalOpen(false);
    await submitPatientInfo(pendingValues);
    setPendingValues(null);
  };

  const handleWarningCancel = () => {
    setIsWarningModalOpen(false);
  };

  return (
    <div className="step-patient-info">
      <h2 className="form-title text-white" style={{ padding: "10px" }}>
        {formData?.service?.visit_type}
      </h2>

      <Form
        form={form}
        layout="vertical"
        initialValues={formData}
        onFinish={handleFinish}
        className="form-container"
      >
        {patientFields.map((field) => {
          // Country is fixed to the US and submitted automatically — no input shown
          if (field.name === "country") {
            return null;
          }

          // ✅ Mobile number with formatting
          if (field.name === "mobile") {
            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                normalize={(value) => {
                  if (!value) return value;
                  return String(value).replace(/\D/g, "").slice(0, 10);
                }}
                rules={[
                  { required: true, message: "Please enter Mobile" },
                  {
                    validator: (_, value) => {
                      const digits = (value || "").replace(/\D/g, "");
                      
                      if (digits.length === 10) {
                        return Promise.resolve();
                      }
                      if (digits.length > 10) {
                        return Promise.reject(
                          "Mobile number must be exactly 10 digits"
                        );
                      }
                      return Promise.reject(
                        "Mobile number must be 10 digits"
                      );
                    },
                  },
                ]}
              >
                <Input
                  type="tel"
                  inputMode="numeric"
                  placeholder="XXXXXXXXXX"
                  maxLength={10}
                  onChange={handlePhoneChange}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = (e.clipboardData || window.clipboardData).getData("text");
                    const currentValue = form.getFieldValue("mobile") || "";
                    const currentDigits = String(currentValue).replace(/\D/g, "");
                    const pastedDigits = String(pastedText).replace(/\D/g, "");
                    const remainingSlots = Math.max(0, 10 - currentDigits.length);
                    const digitsToAdd = pastedDigits.slice(0, remainingSlots);
                    if (digitsToAdd.length > 0) {
                      form.setFieldsValue({ mobile: (currentDigits + digitsToAdd).slice(0, 10) });
                    }
                  }}
                  onKeyDown={(e) => {
                    const currentValue = e.target.value || "";
                    const digits = currentValue.replace(/\D/g, "");
                    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Tab"].includes(e.key)) {
                      return;
                    }
                    if (e.ctrlKey || e.metaKey) {
                      return;
                    }
                    if (["Backspace", "Delete"].includes(e.key)) {
                      return;
                    }
                    if (!/^\d$/.test(e.key)) {
                      e.preventDefault();
                      return;
                    }
                    if (digits.length >= 10) {
                      e.preventDefault();
                    }
                  }}
                  onBeforeInput={(e) => {
                    const inputType = e.data;
                    if (inputType && !/^\d$/.test(inputType)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                  onInput={(e) => {
                    const value = e.target.value;
                    const digits = String(value).replace(/\D/g, "").slice(0, 10);
                    if (digits !== value) {
                      form.setFieldsValue({ mobile: digits });
                    }
                  }}
                />
              </Form.Item>
            );
          }

          // ✅ Date of Birth formatting (MM-DD-YYYY)
          if (field.name === "dob") {
            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={[
                  { required: true, message: "Please enter Date of Birth" },
                  {
                    pattern: /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/,
                    message: "Use valid MM-DD-YYYY format",
                  },
                ]}
              >
                <Input
                  placeholder="MM-DD-YYYY"
                  maxLength={10}
                  onChange={handleDobChange}
                />
              </Form.Item>
            );
          }

          // ✅ Gender radio buttons
          if (field.type === "radio") {
            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={[{ required: field.required, message: `Please select ${field.label}` }]}
              >
                <Radio.Group>
                  {field.options.map((opt) => (
                    <Radio key={opt} value={opt}>
                      {opt}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            );
          }

          // ✅ Text areas (address lines)
          if (field.type === "textarea") {
            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={[{ required: field.required, message: `Please enter ${field.label}` }]}
              >
                <TextArea rows={3} />
              </Form.Item>
            );
          }

          // ✅ Zip Code validation
          if (field.name === "zip_code") {
            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={[
                  { required: true, message: "Please enter Zip Code" },
                  { pattern: /^\d{5}$/, message: "Zip code must be exactly 5 digits" },
                ]}
              >
                <Input
                  type="text"
                  maxLength={5}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, "").slice(0, 5);
                  }}
                />
              </Form.Item>
            );
          }

          // ✅ State as plain text input
          if (field.name === "state") {
            return (
              <Form.Item
                key={field.name}
                name={field.name}
                label={
                  <span>
                    State
                  </span>
                }
                rules={[{ required: true, message: "Please enter your state" }]}
              >
                <Input placeholder="Enter your state" />
              </Form.Item>
            );
          }

          // ✅ Default input for remaining text fields
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={field.label}
              rules={[{ required: field.required, message: `Please enter ${field.label}` }]}
            >
              <Input type={field.type} />
            </Form.Item>
          );
        })}

        <p
          style={{
            fontWeight: 700,
            color: "#fff",
            marginBottom: "1rem",
            lineHeight: 1.5,
          }}
        >
          Note: Please continue only if you have your medical details ready and can
          spend about 10 minutes completing the questionnaire. Exiting before
          confirming an appointment may lead to technical issues.
        </p>

        {/* Navigation Buttons */}
        <Form.Item>
          <div className="form-actions-row">
            <Button onClick={onBack} className="btn-css-class" block>
              Back
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={isLoading}
              className="btn-css"
            >
              Next
            </Button>
          </div>
        </Form.Item>
      </Form>

      <Modal
        title="Please confirm before continuing"
        open={isWarningModalOpen}
        onOk={handleWarningConfirm}
        onCancel={handleWarningCancel}
        okText="Continue"
        cancelText="Cancel"
        confirmLoading={isLoading}
      >
        <p style={{ marginBottom: 0 }}>
          <strong>
            Note: Please continue only if you have your medical details ready
            and can spend about 10 minutes completing the questionnaire.
            Exiting before confirming an appointment may lead to technical
            issues.
          </strong>
        </p>
      </Modal>
    </div>
  );
}
